import React, { useState, useContext, useEffect, useCallback } from 'react';
import classnames from 'classnames';

import { Button, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import { useUserContext, useReviewContext } from '@hydra/common/context';
import DownloadPdfBtn from '@hydra/common/components/ReviewDashboard/DownloadPdfBtn';
import ManageCompetitors from '@hydra/common/components/ReviewDashboard/ManageCompetitors';
import ShareReview from '@hydra/common/components/ReviewDashboard/ShareReview';
import { Icon, Translate, GenerationErrorDialog } from '@hydra/common/components';
import GenerationContext from '@hydra/common/components/GenerationContext';
import { useFeatureFlags } from '@hydra/common/components/FeatureFlags/featureFlagContext';

import redirectToUrl from '@hydra/utils/redirectToUrl';
import { getProjectCount } from '@hydra/utils/overviewApi';
import { getHostname } from '@hydra/utils/urlUtil';
import { sendEvent } from '@hydra/utils/gtm';
import { createProject } from '@hydra/utils/projectApi';
import { getSearchParams } from '@hydra/utils/searchParams';
import { getIsDudaUser } from '@hydra/utils/userUtil';

import { useRefreshReview, useI18n } from '@hydra/common/customHooks';

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    marginTop: theme.spacing(2),
    padding: theme.spacing(1, 2, 0),
    backgroundColor: theme.palette.background.paper,
    borderTop: `1px solid ${theme.palette.grey[200]}`
  },
  extension: {
    justifyContent: 'center',
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'flex-end'
    }
  },
  review: {
    justifyContent: 'center',
    flexDirection: 'column',
    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      justifyContent: 'space-between'
    }
  },
  btnLeft: {
    marginRight: theme.spacing(1),
    [theme.breakpoints.down('xs')]: {
      marginRight: 0,
      display: 'block'
    }
  },
  mobileSpacing: {
    display: 'inline-flex',
    [theme.breakpoints.down('xs')]: {
      marginBottom: theme.spacing(1)
    }
  },
  downloadPdf: {
    [theme.breakpoints.down('xs')]: {
      display: 'none'
    }
  },
  btnMobile: {
    [theme.breakpoints.down('xs')]: {
      width: '100%'
    }
  }
}));

const handleProjectSubmit = async ({ dispatch, url, locale, setGenerationError }) => {
  try {
    dispatch({ type: 'ON_SUBMIT' });
    const data = await createProject(url);
    if (data && data.review) {
      if (data.generation) {
        const generationId = data.generation.id;
        redirectToUrl({
          url: `/${locale}/dashboard/${encodeURIComponent(url)}?${new URLSearchParams({ generationId })}`
        });
      } else {
        redirectToUrl({
          url: `/${locale}/dashboard/${encodeURIComponent(url)}`
        });
      }
    } else if (data && data.errorCode) {
      if (data.errorCode === 'EXISTS') {
        dispatch({ type: 'ON_ERROR_EXISTS' });
      } else if (data.errorCode === 'INVALID_URL') {
        dispatch({ type: 'ON_ERROR_INVALID_URL' });
      } else if (data.errorCode === 'UNRESOLVABLE_URL') {
        dispatch({ type: 'ON_ERROR_UNRESOLVABLE_URL', payload: data });
      }
    } else {
      dispatch({ type: 'ON_ERROR' });
    }
  } catch (error) {
    if (error.errorCode === 'UNRESOLVABLE_URL') {
      setGenerationError(error);
      dispatch({ type: 'ON_ERROR_UNRESOLVABLE_URL', payload: error });
    } else if (error.errorCode === 'EXISTS') {
      redirectToUrl({
        url: `/${locale}/dashboard/${encodeURIComponent(url)}`
      });
    } else {
      dispatch({ type: 'ON_ERROR' });
      throw error;
    }
  }
};

export default function ReviewDashboardFooter ({ competitors, pdfTemplates }) {
  const classes = useStyles();
  const { locale } = useI18n();
  const { user } = useUserContext();
  const featureFlags = useFeatureFlags();
  const allowSubFolder = featureFlags && featureFlags.subfolder;
  const maxProjects = user?.maxProjects || 0;
  const [projectsLeft, setProjectsLeft] = useState(0);
  const { isExtension, isProject, isFree, isAssistant, isShopify } = useReviewContext();
  const [{ isLoading, url }, dispatch] = useContext(GenerationContext);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [generationError, setGenerationError] = useState(null);

  const projectSearchParam = getSearchParams('project');
  const hostname = getHostname(url);
  const encodedUrl = encodeURIComponent(allowSubFolder ? projectSearchParam || hostname : hostname);
  const isDudaUser = getIsDudaUser(user);

  const handleGetProjectsLeft = useCallback(async () => {
    const { count } = (!isFree && !isProject && maxProjects > 0)
      ? await getProjectCount()
      : { count: 0 };
    const projectsLeft = maxProjects - count;
    setProjectsLeft(projectsLeft);
  }, [maxProjects, isFree, isProject]);

  useEffect(() => {
    handleGetProjectsLeft();
  }, [handleGetProjectsLeft]);

  const handleError = error => {
    setGenerationError(error);
    setRefreshLoading(false);
  };

  const refreshReview = useRefreshReview({ url, isFree, handleError });

  const handleRefresh = () => {
    sendEvent('review-actions-refresh-button');
    setRefreshLoading(true);
    refreshReview();
  };

  const handleStartProject = () => {
    sendEvent('review-actions-startproject-button');
    if (!projectsLeft) {
      window.location.href = `/${locale}/user/plan?npl=1`;
      return;
    }
    return handleProjectSubmit({ dispatch, url, locale, setGenerationError });
  };

  if (isExtension) {
    return (
      <div className={classnames(classes.root, classes.extension)}>
        <GenerationErrorDialog
          open={!!generationError}
          onClose={() => setGenerationError(null)}
          error={generationError}
        />
        <Button
          color='primary'
          variant='contained'
          size='small'
          onClick={handleRefresh}
          className={classnames(classes.btnMobile, 'nr-review-refresh')}
        >
          {refreshLoading
            ? <Icon type='loader' size='md' color='white' />
            : <Translate content='dashboard_refresh_review' />}
        </Button>
      </div>
    );
  }

  return (
    <div className={classnames(classes.root, classes.review)}>
      <GenerationErrorDialog
        open={!!generationError}
        onClose={() => setGenerationError(null)}
        error={generationError}
      />
      <div className={classes.mobileSpacing}>
        {!isShopify && (
          <DownloadPdfBtn
            url={url}
            pdfTemplates={pdfTemplates}
            className={classnames(classes.btnLeft, classes.downloadPdf)}
          />
        )}
        {!isFree && (
          <Button
            variant='outlined'
            size='small'
            onClick={handleRefresh}
            className={classnames(classes.btnMobile, 'nr-review-refresh')}
          >
            {refreshLoading
              ? <Icon type='loader' size='md' color='grey' />
              : <Translate content='dashboard_refresh_review' />}
          </Button>
        )}
      </div>
      {isFree && <ShareReview url={url} />}
      {isProject && !isShopify && !isDudaUser && (
        <Button
          variant='outlined'
          size='small'
          href={`/${locale}/settings/${encodedUrl}`}
          {...isAssistant && { target: '_blank' }}
          onClick={() => sendEvent('review-actions-projectsettings-button')}
          className={classnames(classes.btnMobile, 'nr-review-projectsettings-button')}
        >
          <Translate content='pj_settings' />
        </Button>
      )}
      {!isFree && !isProject && !isShopify && !isDudaUser && (
        <div>
          <ManageCompetitors
            url={url}
            competitors={competitors}
            className={classnames(classes.btnLeft, classes.btnMobile, classes.mobileSpacing)}
          />
          {maxProjects > 0 && (
            <Tooltip
              title={(
                <>
                  <strong>{projectsLeft}</strong>
                  &nbsp;
                  <Translate content='project_left' />
                </>
              )}
              placement='bottom'
            >
              <Button
                color='primary'
                variant='contained'
                size='small'
                className={classnames(classes.btnMobile, 'nr-review-startproject-button')}
                onClick={handleStartProject}
              >
                {isLoading
                  ? <Icon type='loader' size='md' color='white' />
                  : <Translate content='start_new_project' />}
              </Button>
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
}
